import { ActionsSheetProps } from "./types"
import React, { useRef, useEffect, useMemo } from "react"
import { ActionSheetIOS, Platform } from "~/ui/primitives"
import ActionsSheetInner from "~/ui/actions-sheet"
import styled from '~/ui/styled'
import { MODAL_MAX_WIDTH } from "~ui/app-shell"

const isIos = Platform.OS === "ios"
const isWeb = Platform.OS === "web"

const Container = styled.div`
  padding: 8px 12px calc(12px + env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
`

const SheetHandle = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.colors.border};
  margin: 4px auto 10px;
`

const ActionGroup = styled.div`
  background-color: ${({ theme }) => theme.colors.blockBackground};
  border-radius: 14px;
  overflow: hidden;
`

const CancelGroup = styled(ActionGroup)`
  margin-top: 8px;
`

const ActionItem = styled.button<{ $destructive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 52px;
  padding: 14px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme, $destructive }) =>
    $destructive ? theme.colors.danger : theme.colors.text};
  box-sizing: border-box;
  transition: background-color 0.15s ease;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.background};
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.background};
  }
`

const CancelItem = styled(ActionItem)`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary};
`

const WebSheetOverlay = styled.div`
  position: fixed;
  inset: 0;
  width: 100%;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`

const WebSheetBackdrop = styled.button`
  position: absolute;
  inset: 0;
  border: none;
  padding: 0;
  margin: 0;
  background-color: rgba(0, 0, 0, 0.45);
  cursor: default;
`

const WebSheetPanel = styled.div`
  position: relative;
  width: 100%;
  max-width: ${MODAL_MAX_WIDTH}px;
  background-color: ${({ theme }) => theme.colors.background};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  max-height: 70vh;
  overflow: auto;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.08);
`

function ActionSheetContent({
  data,
  onSelect,
}: {
  data: ActionsSheetProps["data"]
  onSelect: (index: number) => void
}) {
  const items = data ?? []
  const { actionItems, cancelItem, cancelIndex } = useMemo(() => {
    if (items.length <= 1) {
      return { actionItems: items, cancelItem: null, cancelIndex: -1 }
    }
    return {
      actionItems: items.slice(0, -1),
      cancelItem: items[items.length - 1],
      cancelIndex: items.length - 1,
    }
  }, [items])

  return (
    <Container>
      <SheetHandle aria-hidden />
      {actionItems.length > 0 ? (
        <ActionGroup>
          {actionItems.map((item, index) => (
            <ActionItem
              key={`${item.text}-${index}`}
              type="button"
              $destructive={item.destructive}
              onClick={() => onSelect(index)}
            >
              {item.text}
            </ActionItem>
          ))}
        </ActionGroup>
      ) : null}
      {cancelItem ? (
        <CancelGroup>
          <CancelItem type="button" onClick={() => onSelect(cancelIndex)}>
            {cancelItem.text}
          </CancelItem>
        </CancelGroup>
      ) : null}
    </Container>
  )
}

const Actionsheet: React.FC<ActionsSheetProps> = ({ visible, setVisible, data = [] }) => {
  const ref = useRef<{ setModalVisible?: (visible: boolean) => void }>(null)
  const lastOptionIndex = data.length - 1

  const handleOpenIosActionsSheet = () => {
    ActionSheetIOS.showActionSheetWithOptions(
      {
        options: data.map((i) => i.text),
        cancelButtonIndex: lastOptionIndex,
        destructiveButtonIndex: lastOptionIndex,
      },
      (index) => {
        data[index]?.onClick?.(data[index], index)
      },
    )
    setVisible(false)
  }

  const handleActionSelect = (index: number) => {
    data[index]?.onClick?.(data[index], index)
    setVisible(false)
  }

  useEffect(() => {
    if (isIos) {
      visible && handleOpenIosActionsSheet()
    } else if (!isWeb) {
      ref.current?.setModalVisible?.(visible)
    }
  }, [visible])

  if (isIos) return null

  if (isWeb) {
    if (!visible) return null
    return (
      <WebSheetOverlay>
        <WebSheetBackdrop type="button" aria-label="关闭" onClick={() => setVisible(false)} />
        <WebSheetPanel>
          <ActionSheetContent data={data} onSelect={handleActionSelect} />
        </WebSheetPanel>
      </WebSheetOverlay>
    )
  }

  return (
    <ActionsSheetInner closeOnTouchBackdrop={true} ref={ref} onClose={() => setVisible(false)}>
      <ActionSheetContent data={data} onSelect={handleActionSelect} />
    </ActionsSheetInner>
  )
}

export default Actionsheet
